import * as mediasoupClient from 'mediasoup-client';

/**
 * Per-room mediasoup client state: one Device, one send transport, one receive
 * transport, plus the producers and consumers hanging off them.
 */
export class RoomMedia {
  constructor({ connection, room, onTrack, onTrackEnded, onStateChange, onError }) {
    this.conn = connection;
    this.room = room;
    this.device = null;
    this.sendTransport = null;
    this.recvTransport = null;
    /** local source -> producer  ('mic' | 'camera' | 'screen' | 'screen-audio') */
    this.producers = new Map();
    /** consumerId -> { consumer, info } */
    this.consumers = new Map();
    /** producerId -> consumerId */
    this.byProducer = new Map();
    this.tracks = new Map();

    /** In-flight setup work, so concurrent callers share one attempt. */
    this.pending = new Map();
    /** local source -> the orientation last published for it */
    this.orientations = new Map();

    this.onTrack = onTrack ?? (() => {});
    this.onTrackEnded = onTrackEnded ?? (() => {});
    this.onStateChange = onStateChange ?? (() => {});
    this.onError = onError ?? ((err) => console.error(err));
  }

  get supported() {
    return mediasoupClient.detectDevice() !== undefined;
  }

  /**
   * Runs `create` once even when several callers arrive together, handing them
   * all the same promise.
   *
   * Simultaneous callers are the normal case here, not an edge case: a peer
   * turning on their camera publishes video and audio together, so two
   * `media.producer.new` events arrive back to back and both consume paths ask
   * for the receive transport at once. Without this guard each would create one,
   * and the server keeps only the newest transport per direction - it closes the
   * first, so whichever consume call still held that id failed with
   * transport_not_found ("Unknown transport") and that track never appeared.
   */
  single(key, create) {
    const inFlight = this.pending.get(key);
    if (inFlight) return inFlight;
    const promise = (async () => {
      try {
        return await create();
      } finally {
        this.pending.delete(key);
      }
    })();
    this.pending.set(key, promise);
    return promise;
  }

  async ensureDevice() {
    if (this.device) return this.device;
    return this.single('device', async () => {
      if (this.device) return this.device;
      const { rtpCapabilities } = await this.conn.request('media.capabilities', { room: this.room });
      const device = new mediasoupClient.Device();
      await device.load({ routerRtpCapabilities: rtpCapabilities });
      this.device = device;
      return device;
    });
  }

  async ensureSendTransport() {
    if (this.sendTransport && !this.sendTransport.closed) return this.sendTransport;
    return this.single('send', () => this.openSendTransport());
  }

  async openSendTransport() {
    if (this.sendTransport && !this.sendTransport.closed) return this.sendTransport;
    await this.ensureDevice();
    if (!this.device.canProduce('audio') && !this.device.canProduce('video')) {
      throw new Error('This browser cannot publish media.');
    }

    const params = await this.conn.request('media.transport.create', { room: this.room, direction: 'send' });
    const transport = this.device.createSendTransport({
      id: params.id,
      iceParameters: params.iceParameters,
      iceCandidates: params.iceCandidates,
      dtlsParameters: params.dtlsParameters,
      sctpParameters: params.sctpParameters,
    });

    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.conn
        .request('media.transport.connect', { room: this.room, transportId: transport.id, dtlsParameters })
        .then(callback)
        .catch(errback);
    });

    transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
      this.conn
        .request('media.produce', {
          room: this.room,
          transportId: transport.id,
          kind,
          rtpParameters,
          appData,
        })
        .then(({ id }) => callback({ id }))
        .catch(errback);
    });

    transport.on('connectionstatechange', (state) => {
      this.onStateChange({ direction: 'send', state });
      if (state === 'failed') {
        transport.close();
        this.sendTransport = null;
      }
    });

    this.sendTransport = transport;
    return transport;
  }

  async ensureRecvTransport() {
    if (this.recvTransport && !this.recvTransport.closed) return this.recvTransport;
    return this.single('recv', () => this.openRecvTransport());
  }

  async openRecvTransport() {
    if (this.recvTransport && !this.recvTransport.closed) return this.recvTransport;
    await this.ensureDevice();

    const params = await this.conn.request('media.transport.create', { room: this.room, direction: 'recv' });
    const transport = this.device.createRecvTransport({
      id: params.id,
      iceParameters: params.iceParameters,
      iceCandidates: params.iceCandidates,
      dtlsParameters: params.dtlsParameters,
      sctpParameters: params.sctpParameters,
    });

    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.conn
        .request('media.transport.connect', { room: this.room, transportId: transport.id, dtlsParameters })
        .then(callback)
        .catch(errback);
    });

    transport.on('connectionstatechange', (state) => {
      this.onStateChange({ direction: 'recv', state });
      if (state === 'failed') {
        transport.close();
        this.recvTransport = null;
      }
    });

    this.recvTransport = transport;
    return transport;
  }

  /**
   * Publishes a local track. `source` is 'mic' | 'camera' | 'screen'.
   *
   * `orientation` says how this device is holding its camera; viewers turn the
   * picture to match. It travels in appData because Firefox drops the RTP header
   * extension that would otherwise carry it. See devices.js.
   */
  async publish(track, source, orientation = null) {
    const transport = await this.ensureSendTransport();

    const options = { track, appData: { source, orientation } };
    if (track.kind === 'video') {
      options.encodings =
        source === 'screen'
          ? [{ maxBitrate: 2_500_000, scaleResolutionDownBy: 1 }]
          : [
              { rid: 'low', maxBitrate: 150_000, scaleResolutionDownBy: 4 },
              { rid: 'mid', maxBitrate: 500_000, scaleResolutionDownBy: 2 },
              { rid: 'high', maxBitrate: 1_500_000, scaleResolutionDownBy: 1 },
            ];
      options.codecOptions = { videoGoogleStartBitrate: 1000 };
    } else {
      options.codecOptions = { opusStereo: true, opusDtx: true, opusFec: true };
    }

    const producer = await transport.produce(options);
    this.producers.set(source, producer);
    this.orientations.set(source, orientation);

    producer.on('transportclose', () => this.producers.delete(source));
    producer.observer.on('close', () => this.producers.delete(source));
    track.addEventListener('ended', () => {
      this.unpublish(source).catch(() => {});
    });

    return producer;
  }

  /**
   * Tells the room that this device has been turned. Returns false when there is
   * nothing publishing that source, or nothing changed.
   */
  async setOrientation(source, orientation) {
    const producer = this.producers.get(source);
    if (!producer || producer.closed) return false;
    const last = this.orientations.get(source);
    if (last && last.angle === orientation.angle && last.handheld === orientation.handheld) {
      return false;
    }
    this.orientations.set(source, orientation);
    await this.conn.request('media.producer.orientation', {
      room: this.room,
      producerId: producer.id,
      orientation,
    });
    return true;
  }

  async unpublish(source) {
    const producer = this.producers.get(source);
    if (!producer) return false;
    this.producers.delete(source);
    try {
      await this.conn.request('media.producer.close', { room: this.room, producerId: producer.id });
    } catch {
      /* server may already have closed it */
    }
    try {
      producer.close();
    } catch {
      /* ignore */
    }
    return true;
  }

  /**
   * Mutes or unmutes a local source.
   *
   * Both the producer and the track are toggled: pausing stops the RTP flow and
   * tells the room, while disabling the track makes sure the device stops being
   * sampled at all - which is what a user means by "muted".
   */
  async setPaused(source, paused) {
    const producer = this.producers.get(source);
    if (!producer) return false;

    if (paused) producer.pause();
    else producer.resume();
    if (producer.track) producer.track.enabled = !paused;

    await this.conn.request('media.producer.pause', {
      room: this.room,
      producerId: producer.id,
      paused,
    });
    return true;
  }

  isPublishing(source) {
    const producer = this.producers.get(source);
    return Boolean(producer && !producer.closed);
  }

  isPaused(source) {
    const producer = this.producers.get(source);
    return Boolean(producer && producer.paused);
  }

  /** Subscribes to a remote producer and hands the track to the UI. */
  async consume(info) {
    if (this.byProducer.has(info.producerId)) return null;
    await this.ensureDevice();
    const transport = await this.ensureRecvTransport();

    let params;
    try {
      params = await this.conn.request('media.consume', {
        room: this.room,
        producerId: info.producerId,
        rtpCapabilities: this.device.rtpCapabilities,
        transportId: transport.id,
      });
    } catch (err) {
      // The producer may have vanished between the notification and this call.
      if (err.code === 'producer_not_found') return null;
      throw err;
    }

    const consumer = await transport.consume({
      id: params.id,
      producerId: params.producerId,
      kind: params.kind,
      rtpParameters: params.rtpParameters,
      appData: { source: params.source, peerId: params.peerId },
    });

    this.consumers.set(consumer.id, { consumer, info: params });
    this.byProducer.set(params.producerId, consumer.id);

    consumer.on('transportclose', () => this.dropConsumer(consumer.id));

    await this.conn.request('media.consumer.resume', { room: this.room, consumerId: consumer.id });

    this.onTrack({
      peerId: params.peerId,
      nickname: params.nickname,
      kind: params.kind,
      source: params.source,
      producerId: params.producerId,
      consumerId: consumer.id,
      track: consumer.track,
      paused: params.producerPaused,
      orientation: params.orientation ?? null,
    });
    return consumer;
  }

  dropConsumer(consumerId) {
    const entry = this.consumers.get(consumerId);
    if (!entry) return;
    this.consumers.delete(consumerId);
    this.byProducer.delete(entry.info.producerId);
    try {
      entry.consumer.close();
    } catch {
      /* ignore */
    }
    this.onTrackEnded({
      consumerId,
      producerId: entry.info.producerId,
      peerId: entry.info.peerId,
      source: entry.info.source,
      kind: entry.info.kind,
    });
  }

  dropProducer(producerId) {
    const consumerId = this.byProducer.get(producerId);
    if (consumerId) this.dropConsumer(consumerId);
  }

  /** Subscribes to everything already being published in the room. */
  async consumeExisting(producers) {
    for (const info of producers ?? []) {
      try {
        await this.consume(info);
      } catch (err) {
        this.onError(err);
      }
    }
  }

  close() {
    // Anything still being set up belongs to the old socket; a caller waiting on
    // it must not be handed a transport this close() is about to tear down.
    this.pending.clear();
    for (const [id] of [...this.consumers]) this.dropConsumer(id);
    for (const [source] of [...this.producers]) {
      const producer = this.producers.get(source);
      try {
        producer?.close();
      } catch {
        /* ignore */
      }
    }
    this.producers.clear();
    try {
      this.sendTransport?.close();
    } catch {
      /* ignore */
    }
    try {
      this.recvTransport?.close();
    } catch {
      /* ignore */
    }
    this.sendTransport = null;
    this.recvTransport = null;
    this.device = null;
  }
}

export default RoomMedia;
